import { Button, Drawer } from "@/components";
import { Alert } from "@/components/Alert";
import {
  ArrowLeft,
  CheckSuccess,
  ChevronDown,
  ChevronUp,
  Invoice,
  Minus,
  Plus,
} from "@/components/Icons";
import { Loading } from "@/components/Loading";
import Timer from "@/components/Timer";
import { useColibriTableConfiguration } from "@/hooks/useColibriTableConfiguration";
import { ColibriTable } from "@/services/Colibri";
import { TableStatus } from "@/services/Table";
import styles from "@/styles/containers/command.module.scss";
import { Currency } from "@/tools";
import Bugsnag from "@bugsnag/js";
import classNames from "classnames/bind";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
const css = classNames.bind(styles);

export default function CommandColibri({ onClose }) {
  const [table, setTable] = useState(null);

  const [configurations] = useColibriTableConfiguration();

  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [canCallWaiter, setCanCallWaiter] = useState(true);
  const [callingWaiter, setCallingWaiter] = useState(false);

  const [errorMessage, setErrorMessage] = useState(null);
  const [warningMessage, setWarningMessage] = useState(null);

  const [openClosedTable, setOpenClosedTable] = useState(false);

  const [payersQuantity, setPayersQuantity] = useState(1);

  const totalPerPerson = (table?.total || 0) / (payersQuantity || 1);

  const closed = [TableStatus.CLOSED, TableStatus.PRECLOSED].includes(
    table?.status
  );

  useEffect(() => {
    loadTable();
  }, []);

  const loadTable = async () => {
    setLoading(true);

    try {
      const colibriTable = new ColibriTable();
      const table = await colibriTable.getOverview();
      setTable(table);

      if (table?.payersQuantity) {
        setPayersQuantity(table?.payersQuantity);
      }

      setLoading(false);
    } catch (err) {
      console.log(err);

      Bugsnag.notify(err);

      setErrorMessage(t("unableToLoadAccount"));

      setLoading(false);
    }
  };

  const onCallWaiter = async () => {
    setCallingWaiter(true);
    setAllowCallWaiter(false);

    try {
      const colibriTable = new ColibriTable();
      await colibriTable.callWaiter();

      setWarningMessage({
        title: "O garçom foi chamado",
        subtitle:
          "Espere alguns instantes e um garçom irá atender a sua mesa em breve",
      });

      setCanCallWaiter(false);
    } catch (err) {
      console.log(err);

      Bugsnag.notify(err);

      setErrorMessage(t("unableToCallWaiter"));
    }

    setCallingWaiter(false);
  };

  const t = useTranslations();

  const [allowCallWaiter, setAllowCallWaiter] = useState(true);

  const showCallWaiterButton = configurations.callWaiterSku && !loading && canCallWaiter && table?.items?.length > 0;

  return (
    <div className={styles.commandContainer}>
      <div className={styles.header}>
        <div className={styles.back} onClick={onClose}>
          <ArrowLeft />
        </div>
        <p className={styles.title}>{t("myAccount")}</p>
        {showCallWaiterButton && (
          <Button
            design="default"
            onClick={onCallWaiter}
            disabled={callingWaiter || !allowCallWaiter}
          >
            {t("callWaiter")}
            {!allowCallWaiter && (
              <div className="ml-5">
                <Timer
                  duration={30}
                  start={!allowCallWaiter}
                  onComplete={() => setAllowCallWaiter(true)}
                />
              </div>
            )}
          </Button>
        )}
      </div>

      {loading && (
        <div className={styles.loading}>
          <Loading />
        </div>
      )}

      {!loading && !table?.items?.length && (
        <div className={styles.empty}>
          <Invoice size={34} color="#8C8C8C" />
          <p className={styles.title}>{t("noOrdersYet")}</p>
          <p className={styles.subtitle}>{t("checkOrdersHere")}</p>
        </div>
      )}

      {!loading && table?.items?.length > 0 && (
        <>
          <div className={styles.table}>
            <table>
              <tbody>
                {(table?.items || []).map((item, index) => {
                  return (
                    <tr key={index}>
                      <td>
                        <span>{item.quantity}x</span>
                      </td>
                      <td>
                        <p>{item.name}</p>
                      </td>
                      {/* <td>
                        <p>{Currency.formatCurrency(item.unitPrice)}</p>
                      </td> */}
                      <td>
                        <p>{Currency.formatCurrency(item.totalPrice)}</p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className={styles.footer}>
            <div className={styles.totals}>
              {expanded ? (
                <>
                  <div
                    className="d-flex align-center justify-center w-100 mt-10 mb-20"
                    onClick={() => setExpanded(false)}
                  >
                    <ChevronDown />
                  </div>

                  <div className={styles.item}>
                    <span>{t("subtotal")}</span>
                    <p>{Currency.formatCurrency(table?.subtotal)}</p>
                  </div>

                  {table?.fee ? (
                    <div className={styles.item}>
                      <span>{t("serviceFee")}</span>
                      <p>{Currency.formatCurrency(table.fee)}</p>
                    </div>
                  ) : null}

                  {/* <div className={styles.item}>
                    <span>{t("numberOfPeople")}</span>
                    <div className="d-flex align-center">
                      <div className={styles.quantityContainer}>
                        <span
                          className={css({
                            minus: true,
                            disabled: payersQuantity === 1,
                            enabled: payersQuantity > 1,
                          })}
                          onClick={() =>
                            !closed &&
                            setPayersQuantity((qty) => (qty > 1 ? qty - 1 : 1))
                          }
                        >
                          <Minus />
                        </span>
                        <span className={styles.qty}>{payersQuantity}</span>
                        <span
                          className={css({ plus: true, enabled: true })}
                          onClick={() =>
                            !closed && setPayersQuantity((qty) => qty + 1)
                          }
                        >
                          <Plus />
                        </span>
                      </div>
                      <p>{Currency.formatCurrency(totalPerPerson)}</p>
                    </div>
                  </div> */}
                </>
              ) : null}

              <div className={styles.total}>
                <span>{t("total")}</span>
                {!expanded ? (
                  <div className={styles.chevron}>
                    <div
                      className="d-flex align-center"
                      onClick={() => setExpanded(true)}
                    >
                      <ChevronUp />
                    </div>
                  </div>
                ) : null}
                <p>{Currency.formatCurrency(table?.total)}</p>
              </div>
            </div>

          </div>
        </>
      )}

      <Alert
        title={t("tryAgain")}
        subtitle={errorMessage}
        open={!!errorMessage}
        onClose={() => setErrorMessage(null)}
      />

      {warningMessage && (
        <Alert
          title={warningMessage?.title}
          subtitle={warningMessage?.subtitle}
          open={!!warningMessage}
          onClose={() => setWarningMessage(null)}
        />
      )}

      <Drawer open={openClosedTable} alreadyDrawer direction="bottom-center">
        <div className="d-flex justify-center mb-20">
          <CheckSuccess />
        </div>
        <p className={styles.alertTitle}>{t("tableHasBeenClosed")}</p>
        <p className={styles.alertSubtitle}></p>
        <Button
          design="primary"
          className="w-100"
          onClick={() => setOpenClosedTable(false)}
        >
          {t("returnToTable")}
        </Button>
      </Drawer>
    </div>
  );
}
